import { StatusData } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { TellTalesView } from "@/components/grid/rowDetail/tellTaleDetail/TellTalesComponent";
import { TELLTALES_DIAGNOSTICS } from "@/constants/constants";
import { tellTales } from "@/constants/TellTales";
import { useStatusInfoById } from "@/hooks/statusInfos/useStatusInfoById";
import { useTellTaleSize } from "@/hooks/userState/useTellTaleSize";

const DISPLAY_TELL_TALES = tellTales.filter(item => !item.name.includes("Reserved for"));

interface Props {
  deviceId: string;
  refetchInterval?: number;
}

/**
 * The vehicle's warning lights, as the diagnostic monitor shows them.
 *
 * Read from its status info rather than from the live window: a light is reported when it
 * changes, so one lit for days is in no window at all, and a workshop needs exactly that one.
 */
export function ChecklistTellTales({ deviceId, refetchInterval }: Props) {
  const { t } = useTranslation();
  const { px: size } = useTellTaleSize();
  const { data, isLoading } = useStatusInfoById(deviceId, TELLTALES_DIAGNOSTICS, refetchInterval);
  const statusData = (data?.statusData ?? []) as StatusData[];

  return (
    <Box sx={{ px: 1.5, pb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
        {t("liveData.checklist.tellTales")}
      </Typography>
      {isLoading ? (
        <LinearProgress />
      ) : statusData.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("liveData.checklist.tellTalesNone")}
        </Typography>
      ) : (
        <TellTalesView
          displayTellTales={DISPLAY_TELL_TALES}
          tellTaleData={statusData}
          hideOldData={false}
          size={size}
        />
      )}
    </Box>
  );
}
